/**
 * Authentication Service
 * Handles all authentication-related API calls
 */

import { API_ENDPOINTS } from '../config/api';
import {
  setToken,
  setRefreshToken,
  setUserData,
  clearAuthData,
  UserData,
  setTrustedDeviceToken,
  getTrustedDeviceToken,
} from '../utils/tokenManager';

// Request/Response Types
export interface LoginRequest {
  email: string;
  password: string;
  trustedDeviceToken?: string;
}

export interface RegisterRequest {
  email: string;
  password: string;
  confirmPassword: string;
  firstName: string;
  lastName: string;
}

export interface RegisterUserRequest {
  email: string;
  password: string;
  confirmPassword: string;
  firstName: string;
  lastName: string;
}

// "Get Started" access request — minimal contact info, no password. No user account is
// created; an internal admin invites the actual users later during "Setup & Activate".
export interface RegisterCompanyRequest {
  companyName: string;
  description?: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  streetAddress?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  country?: string;
  // Set true to proceed after the user has confirmed a fuzzy-matched company isn't theirs.
  confirmedNotDuplicate?: boolean;
}

export interface CompanyRequestResponse {
  success: boolean;
  message: string;
  pendingApproval: boolean;
  client: { id: string; name: string };
}

// A possible existing-company match (id/name), used for both exact and fuzzy duplicate hits.
export interface MatchedCompany {
  id: string;
  name: string;
}

// "Request Access" — notifies our team that a user believes their org is already registered.
// Does NOT create any Client/User; it's a notification-only action.
export interface RequestAccessRequest {
  name: string;
  email: string;
  companyName?: string;
  matchedClientId?: string;
}

export interface RequestAccessResponse {
  success: boolean;
  message: string;
}

export interface AuthResponse {
  token?: string; // Optional: not present when account requires activation
  refreshToken?: string;
  expiresAt?: string; // Optional: not present when account requires activation
  mfaRequired?: boolean; // Optional: for backwards compatibility
  user: UserData;
  apiKey?: string;
  apiSecret?: string;
  message?: string; // Optional: present when account requires activation
  trustedDeviceToken?: string; // Optional: returned after MFA with RememberMe
}

// MFA Types
export interface MfaPendingResponse {
  pendingMfaToken: string;
  obfuscatedEmail: string;
  codeExpiresAt: string;
  message: string;
}

export interface VerifyMfaRequest {
  code: string;
  pendingMfaToken: string;
  rememberMe?: boolean;
}

export interface ResendMfaRequest {
  pendingMfaToken: string;
}

export interface ResendMfaResponse {
  success: boolean;
  newPendingMfaToken?: string;
  codeExpiresAt?: string;
  remainingResends: number;
  message: string;
}

/**
 * Login response can be either:
 * - MfaPendingResponse: When MFA is enabled (has pendingMfaToken)
 * - AuthResponse: When MFA is disabled (has token)
 */
export type LoginResponse = MfaPendingResponse | AuthResponse;

/**
 * Type guard to check if login response requires MFA
 */
export const isMfaPendingResponse = (response: LoginResponse): response is MfaPendingResponse => {
  return 'pendingMfaToken' in response && !!response.pendingMfaToken;
};

/**
 * Type guard to check if login response is a direct auth (no MFA)
 */
export const isDirectAuthResponse = (response: LoginResponse): response is AuthResponse => {
  return 'token' in response && !!response.token;
};

export interface ApiError {
  message: string;
  errors?: Record<string, string[]>;
  statusCode?: number;
  // Duplicate-registration catch (POST /api/auth/register/company 409 body). Present only
  // when the backend flagged the signup as a likely duplicate — see AuthController.RegisterCompany.
  duplicate?: boolean;
  matchType?: 'exact' | 'fuzzy';
  matchedCompany?: MatchedCompany | null;
  possibleMatches?: MatchedCompany[];
  canRequestAccess?: boolean;
}

/**
 * Create a custom error from API response
 */
const createApiError = async (response: Response): Promise<ApiError> => {
  let errorData: any;

  try {
    errorData = await response.json();
  } catch {
    errorData = { message: 'An unexpected error occurred' };
  }

  // Extract error message from various possible formats
  // ASP.NET ProblemDetails uses capital-case properties (Title, Detail)
  // Custom errors might use lowercase (message, title)
  const message =
    errorData.detail ||      // ProblemDetails.Detail (most descriptive)
    errorData.Detail ||      // Capital-case variant
    errorData.message ||     // Custom error format
    errorData.title ||       // Lowercase title
    errorData.Title ||       // ProblemDetails.Title
    'An error occurred';

  return {
    message,
    errors: errorData.errors || errorData.Errors,
    statusCode: response.status,
    duplicate: errorData.duplicate,
    matchType: errorData.matchType,
    matchedCompany: errorData.matchedCompany,
    possibleMatches: errorData.possibleMatches,
    canRequestAccess: errorData.canRequestAccess,
  };
};

/**
 * Make authenticated API request
 */
const makeRequest = async (
  url: string,
  options: RequestInit = {}
): Promise<Response> => {
  const defaultHeaders: HeadersInit = {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  };

  const config: RequestInit = {
    ...options,
    headers: {
      ...defaultHeaders,
      ...options.headers,
    },
  };

  try {
    const response = await fetch(url, config);
    return response;
  } catch (error) {
    // Network error
    const networkError: ApiError = {
      message: 'Network error. Please check your connection and try again.',
      statusCode: 0,
    };
    throw networkError;
  }
};

/**
 * Login user - Validates credentials and either:
 * - Returns AuthResponse directly (when MFA is disabled OR trusted device token is valid)
 * - Returns MfaPendingResponse (when MFA is enabled) requiring code verification
 */
export const login = async (credentials: LoginRequest): Promise<LoginResponse> => {
  // Include trusted device token if available (to skip MFA)
  const trustedDeviceToken = getTrustedDeviceToken();
  const requestBody = {
    ...credentials,
    ...(trustedDeviceToken && { trustedDeviceToken }),
  };

  const response = await makeRequest(API_ENDPOINTS.AUTH.LOGIN, {
    method: 'POST',
    body: JSON.stringify(requestBody),
  });

  if (!response.ok) {
    const error = await createApiError(response);
    throw error;
  }

  const data: LoginResponse = await response.json();

  // Check which type of response we received
  if (isDirectAuthResponse(data)) {
    // MFA is disabled - we received a direct auth response with JWT
    // Store authentication data immediately
    setToken(data.token!);
    if (data.refreshToken) {
      setRefreshToken(data.refreshToken);
    }
    setUserData(data.user);
    return data;
  }

  if (isMfaPendingResponse(data)) {
    // MFA is enabled - return pending response for verification step
    return data;
  }

  // Invalid response - neither MFA pending nor direct auth
  throw new Error('Invalid response from server: missing authentication data');
};

/**
 * Verify MFA code - Step 2: Completes login with MFA verification
 * Returns full auth response with JWT token
 * If rememberMe is true and verification succeeds, a trustedDeviceToken is returned
 */
export const verifyMfaCode = async (request: VerifyMfaRequest): Promise<AuthResponse> => {
  const response = await makeRequest(API_ENDPOINTS.AUTH.VERIFY_MFA, {
    method: 'POST',
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    const error = await createApiError(response);
    throw error;
  }

  const data: AuthResponse = await response.json();

  // MFA verification should return a token
  if (!data.token) {
    throw new Error('Invalid response from server: missing authentication token');
  }

  // Store authentication data
  setToken(data.token);
  if (data.refreshToken) {
    setRefreshToken(data.refreshToken);
  }
  setUserData(data.user);

  // Store trusted device token if returned (when RememberMe was checked)
  if (data.trustedDeviceToken) {
    setTrustedDeviceToken(data.trustedDeviceToken);
  }

  return data;
};

/**
 * Resend MFA code
 */
export const resendMfaCode = async (pendingMfaToken: string): Promise<ResendMfaResponse> => {
  const response = await makeRequest(API_ENDPOINTS.AUTH.RESEND_MFA, {
    method: 'POST',
    body: JSON.stringify({ pendingMfaToken }),
  });

  if (!response.ok) {
    const error = await createApiError(response);
    throw error;
  }

  return await response.json();
};

/**
 * Register new user (general registration)
 */
export const register = async (userData: RegisterRequest): Promise<AuthResponse> => {
  const response = await makeRequest(API_ENDPOINTS.AUTH.REGISTER, {
    method: 'POST',
    body: JSON.stringify(userData),
  });

  if (!response.ok) {
    const error = await createApiError(response);
    throw error;
  }

  const data: AuthResponse = await response.json();

  // Only store authentication data if token is present (user is activated)
  if (data.token) {
    setToken(data.token);
    if (data.refreshToken) {
      setRefreshToken(data.refreshToken);
    }
    setUserData(data.user);
  }

  return data;
};

/**
 * Register new individual user (returns API key)
 */
export const registerUser = async (userData: RegisterUserRequest): Promise<AuthResponse> => {
  const response = await makeRequest(API_ENDPOINTS.AUTH.REGISTER_USER, {
    method: 'POST',
    body: JSON.stringify(userData),
  });

  if (!response.ok) {
    const error = await createApiError(response);
    throw error;
  }

  const data: AuthResponse = await response.json();

  // Only store authentication data if token is present (user is activated)
  if (data.token) {
    setToken(data.token);
    if (data.refreshToken) {
      setRefreshToken(data.refreshToken);
    }
    setUserData(data.user);
  }

  return data;
};

/**
 * Register new company (returns API key)
 */
export const registerCompany = async (companyData: RegisterCompanyRequest): Promise<CompanyRequestResponse> => {
  const response = await makeRequest(API_ENDPOINTS.AUTH.REGISTER_COMPANY, {
    method: 'POST',
    body: JSON.stringify(companyData),
  });

  if (!response.ok) {
    const error = await createApiError(response);
    throw error;
  }

  // No auth/token here — this only files a request; activation happens later via invite.
  return (await response.json()) as CompanyRequestResponse;
};

/**
 * Request access to an existing/likely-existing company (duplicate-registration catch on the
 * signup wizard). Notification-only — does NOT create a Client, a User, or any workflow record.
 */
export const requestAccess = async (request: RequestAccessRequest): Promise<RequestAccessResponse> => {
  const response = await makeRequest(API_ENDPOINTS.AUTH.REQUEST_ACCESS, {
    method: 'POST',
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    const error = await createApiError(response);
    throw error;
  }

  return (await response.json()) as RequestAccessResponse;
};

/**
 * Logout user
 */
export const logout = async (): Promise<void> => {
  try {
    // Optionally call logout endpoint if it exists
    await makeRequest(API_ENDPOINTS.AUTH.LOGOUT, {
      method: 'POST',
    });
  } catch (error) {
    // Ignore errors on logout
    console.error('Logout error:', error);
  } finally {
    // Always clear local auth data
    clearAuthData();
  }
};

/**
 * Get user-friendly error message
 */
export const getErrorMessage = (error: unknown): string => {
  if (typeof error === 'object' && error !== null) {
    const apiError = error as ApiError;

    // Handle validation errors
    if (apiError.errors) {
      const firstErrorKey = Object.keys(apiError.errors)[0];
      const firstError = apiError.errors[firstErrorKey]?.[0];
      if (firstError) {
        return firstError;
      }
    }

    // Handle status-specific errors
    // For 401, preserve the backend message (could be "Account inactive", "Invalid email or password", etc.)
    if (apiError.statusCode === 401) {
      return apiError.message || 'Invalid email or password';
    }

    if (apiError.statusCode === 409) {
      return 'An account with this email already exists';
    }

    if (apiError.statusCode === 400) {
      return apiError.message || 'Invalid request. Please check your information and try again.';
    }

    if (apiError.statusCode === 500) {
      return 'Server error. Please try again later.';
    }

    if (apiError.statusCode === 0) {
      return apiError.message || 'Network error. Please check your connection.';
    }

    // Return API error message
    if (apiError.message) {
      return apiError.message;
    }
  }

  // Default error message
  return 'An unexpected error occurred. Please try again.';
};

// Password Reset Types
export interface ForgotPasswordRequest {
  email: string;
}

export interface ForgotPasswordResponse {
  success: boolean;
  message: string;
}

export interface ValidateResetTokenResponse {
  isValid: boolean;
  email?: string;
  errorMessage?: string;
}

export interface ResetPasswordRequest {
  token: string;
  newPassword: string;
  confirmPassword: string;
}

export interface ResetPasswordResponse {
  success: boolean;
  message: string;
}

// Email Verification Types
export interface VerifyEmailResponse {
  success: boolean;
  message: string;
  loginUrl?: string;
}

export interface ResendVerificationRequest {
  email: string;
}

export interface ResendVerificationResponse {
  success: boolean;
  message: string;
}

// Email Not Verified Error (returned from login)
export interface EmailNotVerifiedError {
  status: number;
  title: string;
  detail: string;
  isEmailVerified: boolean;
  email: string;
}

/**
 * Request password reset email
 */
export const forgotPassword = async (data: ForgotPasswordRequest): Promise<ForgotPasswordResponse> => {
  const response = await makeRequest(API_ENDPOINTS.AUTH.FORGOT_PASSWORD, {
    method: 'POST',
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const error = await createApiError(response);
    throw error;
  }

  return await response.json();
};

/**
 * Validate a password reset token
 */
export const validateResetToken = async (token: string): Promise<ValidateResetTokenResponse> => {
  const response = await makeRequest(API_ENDPOINTS.AUTH.VALIDATE_RESET_TOKEN(token), {
    method: 'GET',
  });

  // Always returns 200, check isValid in response
  return await response.json();
};

/**
 * Reset password with token
 */
export const resetPassword = async (data: ResetPasswordRequest): Promise<ResetPasswordResponse> => {
  const response = await makeRequest(API_ENDPOINTS.AUTH.RESET_PASSWORD, {
    method: 'POST',
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const error = await createApiError(response);
    throw error;
  }

  return await response.json();
};

/**
 * Verify email with token
 */
export const verifyEmail = async (token: string): Promise<VerifyEmailResponse> => {
  const response = await makeRequest(API_ENDPOINTS.AUTH.VERIFY_EMAIL(token), {
    method: 'GET',
  });

  // Always returns 200, check success in response
  return await response.json();
};

/**
 * Resend verification email
 */
export const resendVerification = async (data: ResendVerificationRequest): Promise<ResendVerificationResponse> => {
  const response = await makeRequest(API_ENDPOINTS.AUTH.RESEND_VERIFICATION, {
    method: 'POST',
    body: JSON.stringify(data),
  });

  // Always returns 200
  return await response.json();
};

/**
 * Refresh authentication token
 * Called automatically by apiService when token is about to expire
 */
export const refreshToken = async (): Promise<AuthResponse | null> => {
  const { getToken } = await import('../utils/tokenManager');
  const currentToken = getToken();

  if (!currentToken) {
    return null;
  }

  try {
    const response = await fetch(API_ENDPOINTS.AUTH.REFRESH, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'Authorization': `Bearer ${currentToken}`,
      },
    });

    if (!response.ok) {
      // Token refresh failed - user will need to re-authenticate
      console.warn('Token refresh failed:', response.status);
      return null;
    }

    const data: AuthResponse = await response.json();

    if (data.token) {
      // Update stored token
      setToken(data.token);
      if (data.refreshToken) {
        setRefreshToken(data.refreshToken);
      }
      setUserData(data.user);
      return data;
    }

    return null;
  } catch (error) {
    console.error('Token refresh error:', error);
    return null;
  }
};
